import { CstNode, parse } from './parser'
import { tokenize } from './tokenizer'

export interface ParseDiff {
  equal: [CstNode, CstNode][]
  added: CstNode[]
  moved: [CstNode, CstNode][]
  changed: [CstNode, CstNode][]
  removed: CstNode[]
  literals: [CstNode, CstNode][]
}

export function createDiffer(): ParseDiff {
  const diff: ParseDiff = {
    equal: [],
    added: [],
    moved: [],
    changed: [],
    removed: [],
    literals: [],
  }
  return diff
}

export function differ(a: CstNode<'prog'> | null, b: CstNode<'prog'>, diff: ParseDiff = createDiffer()): ParseDiff {
  if (a === null) a = { type: 'prog', text: 'prog', stack: [] } as unknown as CstNode<'prog'>
  const prev = flatten(a)
  const next = flatten(b)

  const pm = createMap([...prev])
  const nm = createMap([...next])

  for (const p of [...prev]) {
    const pid = pm.rev.get(p)
    const n = nm.map.get(pid)?.shift()
    if (n) {
      next.delete(n)
      prev.delete(p)
      // if (pm.rev.get(p) !== nm.rev.get(n)) {
      //   diff.changed.push([p, n])
      //   // console.log(p.stack.length === n.stack.length)
      // }
      // else
      if (p.parent?.indexOf(p)
        !== n.parent?.indexOf(n)
      ) {
        diff.moved.push([p, n])
      }
      else {
        diff.equal.push([p, n])
      }
    }
  }

  outer: for (const n of [...next]) {
    for (const p of prev) {
      next.delete(n)
      prev.delete(p)

      // if we only changed number literals, populate .literals so we avoid a compile
      const changes = diffNode(p, n)
      if (changes.length && changes.every(([p, n]) =>
        p.type === 'number' && n.type === 'number'
      )) {
        diff.literals.push(...changes)
        diff.equal.push([p, n])
      }
      else {
        diff.changed.push([p, n])
      }

      continue outer
    }
    diff.added.push(n)
  }

  diff.removed.push(...prev)

  return diff
}

function flatten(root: CstNode<'prog'>) {
  const result: CstNode[] = []
  const queue: CstNode[] = [root]
  while (queue.length > 0) {
    const node = queue.shift()!
    if (['scoped', 'array', 'prog', 'label', 'op'].includes(node.type)) {
      result.push(node)
    }
    if ('stack' in node) queue.push(...node.stack)
  }
  return new Set(result)
}

function createMap(x: CstNode[]) {
  const map = new Map()
  const rev = new Map()
  for (const node of x) {
    const id = `${serialize(node)}`
    if (map.has(id)) {
      map.get(id).push(node)
    }
    else {
      map.set(id, [node])
    }
    rev.set(node, id)
  }
  return { map, rev }
}

function diffNode(p: CstNode, n: CstNode, changes: [CstNode, CstNode][] = []) {
  if ('stack' in p && 'stack' in n && p.stack.length === n.stack.length) {
    for (let i = 0, pn: CstNode, nn: CstNode; i < p.stack.length; i++) {
      pn = p.stack[i]
      nn = n.stack[i]
      diffNode(pn, nn, changes)
    }
  }
  // TODO: what happens when p.stack.length !== n.stack.length?

  if (
    p.type !== n.type ||
    ('text' in p && 'text' in n && p.text !== n.text) // id,number,op
  ) {
    changes.push([p, n])
  }

  return changes
}

export function serialize(x: CstNode, depth = 0): string {
  return `${x.text}${depth <= 1 && 'stack' in x ? `(${x.stack.map(y => serialize(y, depth + 1))})` : ''}`
}

export function test() {
  type TestNode = { text?: string, stack?: TestNode[] }

  // @env browser
  const passing: [string, string, { [K in keyof ParseDiff]: (TestNode | TestNode[])[] }][] = [
    ['', '[sin 1]', {
      equal: [],
      added: [
        { text: ']', stack: [{ text: 'sin' }, { text: '1' }] },
      ],
      changed: [[
        { text: 'prog' },
        { text: 'prog' },
      ]],
      removed: [],
      moved: [],
      literals: [],
    }],
    ['[sin 1]', '[sin 1]', {
      equal: [[
        { text: 'prog' },
        { text: 'prog' },
      ], [
        { text: ']', stack: [{ text: 'sin' }, { text: '1' }] },
        { text: ']', stack: [{ text: 'sin' }, { text: '1' }] },
      ]],
      added: [],
      changed: [],
      removed: [],
      moved: [],
      literals: [],
    }],
    ['[sin 1]', '[sin 1].1*', {
      equal: [[
        { text: ']', stack: [{ text: 'sin' }, { text: '1' }] },
        { text: ']', stack: [{ text: 'sin' }, { text: '1' }] },
      ]],
      added: [],
      changed: [[
        {
          text: 'prog', stack: [
            { text: ']' }
          ]
        },
        {
          text: 'prog', stack: [
            { text: ']' },
            { text: '.1' },
            { text: '*' },
          ]
        },
      ]],
      removed: [],
      moved: [],
      literals: [],
    }],
    ['[sin 1].1*', '[sin 1].2*', {
      equal: [[
        { text: ']', stack: [{ text: 'sin' }, { text: '1' }] },
        { text: ']', stack: [{ text: 'sin' }, { text: '1' }] },
      ], [
        { text: 'prog' },
        { text: 'prog' },
      ]],
      added: [],
      changed: [],
      removed: [],
      moved: [],
      literals: [[
        { text: '.1' },
        { text: '.2' },
      ]],
    }],
    ['[sin 1]', '[sin 2]', {
      equal: [[
        { text: 'prog' },
        { text: 'prog' },
      ], [
        { text: ']', stack: [{ text: 'sin' }, { text: '1' }] },
        { text: ']', stack: [{ text: 'sin' }, { text: '2' }] },
      ]],
      added: [],
      changed: [],
      removed: [],
      moved: [],
      literals: [[
        { text: '1' },
        { text: '2' },
      ]],
    }],
    ['[sin 1]', '[sin a]', {
      equal: [[
        { text: 'prog' },
        { text: 'prog' },
      ]],
      added: [],
      changed: [[
        { text: ']', stack: [{ text: 'sin' }, { text: '1' }] },
        { text: ']', stack: [{ text: 'sin' }, { text: 'a' }] },
      ]],
      removed: [],
      moved: [],
      literals: [],
    }],
    ['[sin 1 [sin 2]]', '[sin 1 [sin 3]]', {
      equal: [[
        { text: 'prog' },
        { text: 'prog' },
      ], [
        { text: ']', stack: [{ text: 'sin' }, { text: '1' }, { text: ']', stack: [{ text: 'sin' }, { text: '2' }] }] },
        { text: ']', stack: [{ text: 'sin' }, { text: '1' }, { text: ']', stack: [{ text: 'sin' }, { text: '3' }] }] },
      ], [
        { text: ']', stack: [{ text: 'sin' }, { text: '2' }] },
        { text: ']', stack: [{ text: 'sin' }, { text: '3' }] },
      ]],
      added: [],
      changed: [],
      removed: [],
      moved: [],
      literals: [[
        { text: '2' },
        { text: '3' },
      ]],
    }],
    ['[sin 1 [sin 2]]', '[sin 1 2 [sin 2]]', {
      equal: [[
        { text: 'prog' },
        { text: 'prog' },
      ]],
      added: [],
      changed: [[
        { text: ']', stack: [{ text: 'sin' }, { text: '1' }, { text: ']', stack: [{ text: 'sin' }, { text: '2' }] }] },
        { text: ']', stack: [{ text: 'sin' }, { text: '1' }, { text: '2' }, { text: ']', stack: [{ text: 'sin' }, { text: '2' }] }] },
      ]],
      removed: [],
      moved: [
        [
          { text: ']', stack: [{ text: 'sin' }, { text: '2' }] },
          { text: ']', stack: [{ text: 'sin' }, { text: '2' }] },
        ]
      ],
      literals: [],
    }],
    ['[sin 1 2 [sin 2]]', '[sin 1 [sin 2]]', {
      equal: [[
        { text: 'prog' },
        { text: 'prog' },
      ]],
      added: [],
      changed: [[
        { text: ']', stack: [{ text: 'sin' }, { text: '1' }, { text: '2' }, { text: ']', stack: [{ text: 'sin' }, { text: '2' }] }] },
        { text: ']', stack: [{ text: 'sin' }, { text: '1' }, { text: ']', stack: [{ text: 'sin' }, { text: '2' }] }] },
      ]],
      removed: [],
      moved: [
        [
          { text: ']', stack: [{ text: 'sin' }, { text: '2' }] },
          { text: ']', stack: [{ text: 'sin' }, { text: '2' }] },
        ]
      ],
      literals: [],
    }],
    ['[sin 1 [sin 2]]', '[sin 2 [sin 2]]', {
      equal: [[
        { text: 'prog' },
        { text: 'prog' },
      ], [
        { text: ']', stack: [{ text: 'sin' }, { text: '2' }] },
        { text: ']', stack: [{ text: 'sin' }, { text: '2' }] },
      ], [
        { text: ']', stack: [{ text: 'sin' }, { text: '1' }, { text: ']', stack: [{ text: 'sin' }, { text: '2' }] }] },
        { text: ']', stack: [{ text: 'sin' }, { text: '2' }, { text: ']', stack: [{ text: 'sin' }, { text: '2' }] }] },
      ]],
      added: [],
      changed: [],
      removed: [],
      moved: [],
      literals: [[
        { text: '1' },
        { text: '2' },
      ]],
    }],
    ['[sin 1]', '[sin 1] [sin 2]', {
      equal: [[
        { text: ']', stack: [{ text: 'sin' }, { text: '1' }] },
        { text: ']', stack: [{ text: 'sin' }, { text: '1' }] },
      ]],
      added: [
        { text: ']', stack: [{ text: 'sin' }, { text: '2' }] },
      ],
      changed: [[
        { text: 'prog' },
        { text: 'prog' },
      ]],
      removed: [],
      moved: [],
      literals: [],
    }],
    ['[sin 1] [sin 2]', '[sin 1] [sin 3] [sin 2]', {
      equal: [[
        { text: ']', stack: [{ text: 'sin' }, { text: '1' }] },
        { text: ']', stack: [{ text: 'sin' }, { text: '1' }] },
      ]],
      added: [
        { text: ']', stack: [{ text: 'sin' }, { text: '3' }] },
      ],
      changed: [[
        { text: 'prog' },
        { text: 'prog' },
      ]],
      removed: [],
      moved: [[
        { text: ']', stack: [{ text: 'sin' }, { text: '2' }] },
        { text: ']', stack: [{ text: 'sin' }, { text: '2' }] },
      ]],
      literals: [],
    }],

    ['[sin 1] [sin 2]', '[sin 2] [sin 1]', {
      equal: [[
        { text: 'prog' },
        { text: 'prog' },
      ]],
      added: [],
      changed: [],
      removed: [],
      moved: [
        [
          { text: ']', stack: [{ text: 'sin' }, { text: '1' }] },
          { text: ']', stack: [{ text: 'sin' }, { text: '1' }] },
        ],
        [
          { text: ']', stack: [{ text: 'sin' }, { text: '2' }] },
          { text: ']', stack: [{ text: 'sin' }, { text: '2' }] },
        ],
      ],
      literals: [],
    }],
    ['[sin 1] [sin 2]', '[sin 2]', {
      equal: [],
      added: [],
      changed: [[
        { text: 'prog' },
        { text: 'prog' },
      ]],
      removed: [
        { text: ']', stack: [{ text: 'sin' }, { text: '1' }] },
      ],
      moved: [
        [
          { text: ']', stack: [{ text: 'sin' }, { text: '2' }] },
          { text: ']', stack: [{ text: 'sin' }, { text: '2' }] },
        ],
      ],
      literals: [],
    }],
    ['[sin 1] [sin 2]', '[sin 1 [sin 2]]', {
      equal: [],
      added: [],
      changed: [
        [
          { text: 'prog' },
          { text: 'prog' },
        ],
        [
          { text: ']', stack: [{ text: 'sin' }, { text: '1' }] },
          { text: ']', stack: [{ text: 'sin' }, { text: '1' }, { text: ']', stack: [{ text: 'sin' }, { text: '2' }] }] },
        ]
      ],
      removed: [],
      moved: [
        [
          { text: ']', stack: [{ text: 'sin' }, { text: '2' }] },
          { text: ']', stack: [{ text: 'sin' }, { text: '2' }] },
        ],
      ],
      literals: [],
    }],
    ['[sin 1] [sin 2]', '[sin 3] [sin 1] [sin 2]', {
      equal: [],
      added: [
        { text: ']', stack: [{ text: 'sin' }, { text: '3' }] },
      ],
      changed: [[
        { text: 'prog' },
        { text: 'prog' },
      ]],
      removed: [],
      moved: [
        [
          { text: ']', stack: [{ text: 'sin' }, { text: '1' }] },
          { text: ']', stack: [{ text: 'sin' }, { text: '1' }] },
        ],
        [
          { text: ']', stack: [{ text: 'sin' }, { text: '2' }] },
          { text: ']', stack: [{ text: 'sin' }, { text: '2' }] },
        ],
      ],
      literals: [],
    }],
    [
      '[sin 1] [sin 1]',
      '[sin 1] [sin 1]',
      {
        equal: [[
          { text: 'prog' },
          { text: 'prog' },
        ],
        [
          { text: ']', stack: [{ text: 'sin' }, { text: '1' }] },
          { text: ']', stack: [{ text: 'sin' }, { text: '1' }] },
        ],
        [
          { text: ']', stack: [{ text: 'sin' }, { text: '1' }] },
          { text: ']', stack: [{ text: 'sin' }, { text: '1' }] },
        ],
        ],
        added: [],
        changed: [],
        removed: [],
        moved: [],
        literals: [],
      }]
  ]


  describe('compare', () => {
    it(`null == [sin 1]`, () => {
      const b = parse([...tokenize('[sin 1]')])
      const res = differ(null, b)
      expect(res).toMatchObject({
        equal: [],
        added: [
          {
            type: 'scoped', stack: [
              { text: 'sin' },
              { text: '1' },
            ]
          }
        ],
        changed: [
          [{ type: 'prog' }, { type: 'prog' }]
        ],
        removed: [],
        moved: []
      })
    })

    passing.forEach(([ta, tb, expected]) => {
      it(`${ta} == ${tb}`, () => {
        const a = parse([...tokenize(ta)])
        const b = parse([...tokenize(tb)])
        const res = differ(a, b)
        expect(res).toMatchObject(expected)
      })
    })
  })
}
